import { useMemo } from "react";
import { TreeViewBaseItem } from "@mui/x-tree-view/models";

/**
 * Hook that manages tree relationships between items
 * @param items Tree items to analyze
 * @returns Object containing relationship maps and helper functions
 */
export function useTreeRelations(items: TreeViewBaseItem[]) {
  // Build maps of relationships between items
  const { itemsMap, childrenMap, parentMap } = useMemo(() => {
    const itemsMap = new Map<string, TreeViewBaseItem>();
    const childrenMap = new Map<string, string[]>();
    const parentMap = new Map<string, string>();

    // Helper function to recursively build the maps
    const buildMaps = (items: TreeViewBaseItem[], parentId?: string) => {
      for (const item of items) {
        itemsMap.set(item.id, item);

        if (parentId) {
          parentMap.set(item.id, parentId);
          const parentChildren = childrenMap.get(parentId) || [];
          childrenMap.set(parentId, [...parentChildren, item.id]);
        }

        if (item.children && item.children.length > 0) {
          buildMaps(item.children, item.id);
        }
      }
    };

    buildMaps(items);

    return { itemsMap, childrenMap, parentMap };
  }, [items]);

  /**
   * Gets all descendants of a given item
   * @param itemId ID of the item
   * @returns Array of descendant IDs
   */
  const getAllDescendants = (itemId: string): string[] => {
    const children = childrenMap.get(itemId) || [];
    const descendants = [...children];

    for (const childId of children) {
      descendants.push(...getAllDescendants(childId));
    }

    return descendants;
  };

  /**
   * Gets all ancestors of a given item
   * @param itemId ID of the item
   * @returns Array of ancestor IDs
   */
  const getAncestors = (itemId: string): string[] => {
    const ancestors: string[] = [];
    let currentId = itemId;

    while (parentMap.has(currentId)) {
      const parentId = parentMap.get(currentId);
      if (!parentId) break;

      ancestors.push(parentId);
      currentId = parentId;
    }

    return ancestors;
  };

  /**
   * Checks if all children of a parent are in a selection array
   * @param parentId ID of the parent item
   * @param selected Array of selected item IDs
   * @returns true if all children are selected
   */
  const areAllChildrenSelected = (
    parentId: string,
    selected: string[]
  ): boolean => {
    const children = childrenMap.get(parentId) || [];
    return (
      children.length > 0 &&
      children.every((childId) => selected.includes(childId))
    );
  };

  /**
   * Filters a list of IDs to show only the highest items in each hierarchy branch
   * @param ids Array of item IDs to filter
   * @returns Array containing only the highest items in hierarchy
   */
  const filterToHighestAncestors = (ids: string[]): string[] => {
    if (ids.length === 0) return [];

    const idsSet = new Set(ids);

    // Use native array filter to keep only items that should be displayed
    return ids.filter((id) => {
      // Remove if any ancestor is selected
      // (We only want to show the highest level selected item)
      const ancestors = getAncestors(id);
      return !ancestors.some((ancestorId) => idsSet.has(ancestorId));
    });
  };

  return {
    itemsMap,
    childrenMap,
    parentMap,
    getAllDescendants,
    getAncestors,
    areAllChildrenSelected,
    filterToHighestAncestors,
  };
}
